/**
 * HH Finance Module - API Service
 *
 * Connects to the HH Finance Agent REST API (port 19792)
 */

import type {
  HealthStatus,
  DashboardSummary,
  FinancialDashboard,
  KeyMetrics,
  BankAccount,
  AccountSummary,
  CustomerSummary,
  VendorSummary,
  InvoiceSummary,
  BillSummary,
  AgingBucket,
  SchedulerStatus,
  ScheduledJob,
  JobExecution,
} from '../types';

// API Configuration
//
// REACT_APP_HH_FINANCE_API_URL is threaded into the bundle by webpack
// (DefinePlugin in webpack.config.js). In production builds where the env
// var is not set, this resolves to '' — we detect that and surface a clear
// error rather than silently falling through to localhost.
const API_BASE_URL: string = (process.env.REACT_APP_HH_FINANCE_API_URL || '').trim();

let hasWarnedMissingBaseUrl = false;
function warnIfMissingBaseUrl(): void {
  if (!API_BASE_URL && !hasWarnedMissingBaseUrl) {
    hasWarnedMissingBaseUrl = true;
    // eslint-disable-next-line no-console
    console.error(
      '[hh-finance] REACT_APP_HH_FINANCE_API_URL is not set. ' +
      'The Finance dashboard will not be able to reach the HH Finance Agent. ' +
      'Set the build-time env var on the equa-web Railway service.'
    );
  }
}

// Generic fetch wrapper with error handling
async function apiFetch<T>(endpoint: string, options?: RequestInit): Promise<T> {
  warnIfMissingBaseUrl();
  if (!API_BASE_URL) {
    throw new Error(
      'HH Finance Agent URL is not configured. Contact an administrator.'
    );
  }
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(errorData.error || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * HH Finance API Service
 */
export const hhFinanceApi = {
  // ========== Health ==========
  getHealth: (): Promise<HealthStatus> =>
    apiFetch('/health'),

  // ========== Dashboard ==========
  getDashboard: (): Promise<{ dashboard: FinancialDashboard; metrics: KeyMetrics }> =>
    apiFetch('/api/dashboard'),

  getDashboardSummary: (): Promise<DashboardSummary> =>
    apiFetch('/api/dashboard/summary'),

  // ========== Accounts ==========
  getAccounts: (): Promise<{ accounts: AccountSummary[] }> =>
    apiFetch('/api/accounts'),

  getBankAccounts: (): Promise<{ accounts: BankAccount[] }> =>
    apiFetch('/api/accounts/bank'),

  // ========== Customers / AR ==========
  getCustomers: (): Promise<{ customers: CustomerSummary[] }> =>
    apiFetch('/api/customers'),

  getCustomersWithBalance: (): Promise<{ customers: CustomerSummary[] }> =>
    apiFetch('/api/customers/with-balance'),

  getTotalAR: (): Promise<{ totalAccountsReceivable: number }> =>
    apiFetch('/api/ar/total'),

  getARAging: (): Promise<{ aging: AgingBucket }> =>
    apiFetch('/api/ar/aging'),

  // ========== Vendors / AP ==========
  getVendors: (): Promise<{ vendors: VendorSummary[] }> =>
    apiFetch('/api/vendors'),

  getVendorsWithBalance: (): Promise<{ vendors: VendorSummary[] }> =>
    apiFetch('/api/vendors/with-balance'),

  getTotalAP: (): Promise<{ totalAccountsPayable: number }> =>
    apiFetch('/api/ap/total'),

  getAPAging: (): Promise<{ aging: AgingBucket }> =>
    apiFetch('/api/ap/aging'),

  // ========== Invoices ==========
  getInvoices: (limit?: number): Promise<{ invoices: InvoiceSummary[] }> =>
    apiFetch(`/api/invoices${limit ? `?limit=${limit}` : ''}`),

  getOpenInvoices: (): Promise<{ invoices: InvoiceSummary[] }> =>
    apiFetch('/api/invoices/open'),

  getOverdueInvoices: (): Promise<{ invoices: InvoiceSummary[] }> =>
    apiFetch('/api/invoices/overdue'),

  // ========== Bills ==========
  getBills: (limit?: number): Promise<{ bills: BillSummary[] }> =>
    apiFetch(`/api/bills${limit ? `?limit=${limit}` : ''}`),

  getOpenBills: (): Promise<{ bills: BillSummary[] }> =>
    apiFetch('/api/bills/open'),

  getOverdueBills: (): Promise<{ bills: BillSummary[] }> =>
    apiFetch('/api/bills/overdue'),

  getBillsDueThisWeek: (): Promise<{ bills: BillSummary[] }> =>
    apiFetch('/api/bills/due-this-week'),

  // ========== Reports ==========
  getProfitAndLoss: (startDate?: string, endDate?: string): Promise<{ report: unknown; period: { startDate: string; endDate: string } }> => {
    const params = new URLSearchParams();
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    const query = params.toString() ? `?${params.toString()}` : '';
    return apiFetch(`/api/reports/pnl${query}`);
  },

  getBalanceSheet: (): Promise<{ report: unknown }> =>
    apiFetch('/api/reports/balance-sheet'),

  getAgingReport: (): Promise<{ report: unknown }> =>
    apiFetch('/api/reports/aging'),

  getTopExpenses: (limit?: number): Promise<{ expenses: unknown[] }> =>
    apiFetch(`/api/reports/top-expenses${limit ? `?limit=${limit}` : ''}`),

  getTopIncome: (limit?: number): Promise<{ income: unknown[] }> =>
    apiFetch(`/api/reports/top-income${limit ? `?limit=${limit}` : ''}`),

  // ========== Scheduler ==========
  getSchedulerStatus: (): Promise<SchedulerStatus> =>
    apiFetch('/api/scheduler/status'),

  getScheduledJobs: (): Promise<{ jobs: ScheduledJob[] }> =>
    apiFetch('/api/scheduler/jobs'),

  runJob: (jobId: string): Promise<{ execution: JobExecution }> =>
    apiFetch(`/api/scheduler/jobs/${jobId}/run`, { method: 'POST' }),

  toggleJob: (jobId: string, enabled: boolean): Promise<{ success: boolean; enabled: boolean }> =>
    apiFetch(`/api/scheduler/jobs/${jobId}/toggle`, {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    }),

  startScheduler: (): Promise<{ started: boolean }> =>
    apiFetch('/api/scheduler/start', { method: 'POST' }),

  stopScheduler: (): Promise<{ stopped: boolean }> =>
    apiFetch('/api/scheduler/stop', { method: 'POST' }),

  // ========== Tools ==========
  getTools: (): Promise<{ tools: unknown[] }> =>
    apiFetch('/api/tools'),

  executeTool: (toolName: string, params?: unknown): Promise<{ result: unknown }> =>
    apiFetch(`/api/tools/${toolName}/execute`, {
      method: 'POST',
      body: JSON.stringify(params || {}),
    }),
};

export default hhFinanceApi;
